import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import JsonLd from '../components/JsonLd';
import {
  SITE_URL,
  formatPageTitle,
  formatDescription,
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildWebPageSchema,
  buildSchemaGraph,
} from '../utils/seo';
import Footer from '../Footer';
import './SeoLandingPage.css';

const USE_CASES = [
  { to: '/ai-video-maker', title: 'AI video maker (free, no download)', desc: 'AI video generator + online editor for short-form content.' },
  { to: '/ai-vlog-maker', title: 'AI vlog maker (free, no download)', desc: 'Create vlog-style storytelling edits from raw clips.' },
  { to: '/ai-minivlog-maker', title: 'AI minivlog maker (free, no download)', desc: 'Fast-cut minivlogs with hooks, captions, and music.' },
  { to: '/ai-day-in-the-life-maker', title: 'AI day in the life maker (free, no download)', desc: 'Stitch daily moments into a narrative video.' },
  { to: '/ai-get-ready-with-me-maker', title: 'AI get ready with me maker (free, no download)', desc: 'GRWM videos with better pacing and storytelling.' },
];

const FREE_TOOLS = [
  { to: '/tiktok-shadowban-checker', title: 'TikTok shadowban checker', desc: 'Enter your handle. If you have views, you are not shadowbanned. Fix retention instead.' },
  { to: '/instagram-shadowban-checker', title: 'Instagram shadowban checker', desc: 'Enter your handle. More than 0 views means you need better content, not a ban fix.' },
  { to: '/when-to-post-tiktok', title: 'When to post on TikTok', desc: 'Clear cadence: about 5 TikToks a day while you learn what works.' },
  { to: '/when-to-post-instagram', title: 'When to post on Instagram', desc: 'Clear cadence: about 5 Reels a day while you learn what works.' },
];

export default function UseCasesPage() {
  const canonicalUrl = 'https://www.merra.ai/use-cases';

  return (
    <div className="seo-landing">
      <Helmet>
        <title>{formatPageTitle('Merra AI Use Cases – Video Formats & Workflows')}</title>
        <meta
          name="description"
          content={formatDescription('Explore Merra AI use cases: AI video maker, vlog maker, minivlog maker, day-in-the-life maker, and get-ready-with-me video creation.')}
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={formatPageTitle('Merra AI Use Cases – Video Formats & Workflows')} />
        <meta
          property="og:description"
          content={formatDescription('Explore Merra AI use cases for short-form video creation.')}
        />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>
      <JsonLd
        schema={buildSchemaGraph([
          buildOrganizationSchema(),
          buildWebSiteSchema(),
          buildWebPageSchema({
            name: formatPageTitle('Merra AI Use Cases – Video Formats & Workflows'),
            description: 'Explore Merra AI use cases for short-form video creation.',
            url: `${SITE_URL}/use-cases`,
          }),
        ])}
      />

      <div className="seo-landing__container">
        <div className="seo-landing__nav">
          <Link to="/landing-page" className="seo-landing__brand">
            Merra AI
          </Link>
          <div className="seo-landing__navlinks">
            <Link to="/blog" className="seo-landing__link" target="_blank" rel="noopener noreferrer">
              Blog
            </Link>
            <Link to="/pricing" className="seo-landing__link" target="_blank" rel="noopener noreferrer">
              Pricing
            </Link>
            <Link to="/about" className="seo-landing__link" target="_blank" rel="noopener noreferrer">
              About
            </Link>
          </div>
        </div>

        <div className="seo-landing__heroCard">
          <h1 className="seo-landing__h1">Use cases</h1>
          <p className="seo-landing__sub">
            Pick the workflow you want to create: vlogs, minivlogs, day-in-the-life, GRWM, and more. Each page includes
            a tailored description, FAQs, and the best way to get started.
          </p>
        </div>

        <div className="seo-landing__sections">
          {USE_CASES.map((u) => (
            <div key={u.to} className="seo-landing__section">
              <div className="seo-landing__h2">{u.title}</div>
              <p className="seo-landing__p">{u.desc}</p>
              <div className="seo-landing__related" style={{ marginTop: 14 }}>
                <Link to={u.to} className="seo-landing__relatedLink">
                  Open page →
                </Link>
                <Link to="/landing-page" className="seo-landing__relatedLink">
                  Try Merra AI →
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="seo-landing__heroCard" style={{ marginTop: 28 }}>
          <h2 className="seo-landing__h2" style={{ margin: 0 }}>Free tools</h2>
          <p className="seo-landing__sub">
            Quick checkers for shadowbans and posting cadence. Honest answers that push you toward better content and more trials.
          </p>
        </div>

        <div className="seo-landing__sections">
          {FREE_TOOLS.map((u) => (
            <div key={u.to} className="seo-landing__section">
              <div className="seo-landing__h2">{u.title}</div>
              <p className="seo-landing__p">{u.desc}</p>
              <div className="seo-landing__related" style={{ marginTop: 14 }}>
                <Link to={u.to} className="seo-landing__relatedLink">
                  Open tool →
                </Link>
                <Link to="/landing-page" className="seo-landing__relatedLink">
                  Try Merra AI →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}

